/* eslint-disable react/prop-types */
import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { BriefcaseBusiness, Eye, Plus, Search, X } from "lucide-react";
import SearchPicker from "../../../../components/admin/SearchPicker.jsx";
import { SelectDropdown, StatusBadge, TextInput } from "../../../../components/ui/index.js";
import getMediaUrl from "../../../../components/tools/utils.jsx";
import {
    CUSTOMER_STATE,
    PROJECT_STATUS_OPTIONS,
    PROJECT_STATUS_TONE,
    projectStatusLabel,
} from "../../../../components/catalog/catalogUtils.js";
import ProyectoDetalle from "./ProyectoDetalle.jsx";

const STATUS_FILTERS = [{ value: "", label: "Todos los estados" }, ...PROJECT_STATUS_OPTIONS];

const CUSTOMER_KINDS = [
    { value: "lead", label: "Lead", endpoint: "/leads/", label_of: (o) => o?.company_name || o?.contact_name || o?.email || o?.phone || "" },
    { value: "cliente", label: "Cliente", endpoint: "/clients/", label_of: (o) => o?.company_name || [o?.nombre, o?.apellido].filter(Boolean).join(" ") || o?.email || "" },
    { value: "customer_user", label: "Usuario", endpoint: "/projects/people-options/", label_of: (o) => o?.email || "" },
];

/** Crear un proyecto: nombre y para quién (lead, cliente o usuario). */
function NewProjectModal({ initial, onClose, onCreated }) {
    const [name, setName] = useState("");
    const [kind, setKind] = useState(initial?.kind || "lead");
    const [customer, setCustomer] = useState(initial?.id ? { id: initial.id, label: initial.label || `#${initial.id}` } : null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const config = CUSTOMER_KINDS.find((item) => item.value === kind);

    const create = async (event) => {
        event.preventDefault();
        setSaving(true);
        setError("");
        try {
            const { data } = await axios.post("/projects/", { name, [kind]: customer.id });
            onCreated(data);
        } catch (err) {
            const data = err?.response?.data;
            setError(data?.non_field_errors?.[0] || data?.[kind]?.[0] || data?.name?.[0] || data?.detail || "No se ha podido crear el proyecto.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="modal modal-open" role="dialog" aria-modal="true" aria-labelledby="new-project-title">
            <form className="modal-box flex flex-col gap-4" onSubmit={create}>
                <div className="flex items-center justify-between">
                    <h2 id="new-project-title" className="text-lg font-semibold">Nuevo proyecto</h2>
                    <button type="button" className="btn btn-ghost btn-sm btn-square" onClick={onClose} aria-label="Cerrar">
                        <X className="w-5 h-5" aria-hidden="true" />
                    </button>
                </div>
                <label className="form-control">
                    <span className="label-text text-sm font-medium">Nombre</span>
                    <TextInput value={name} onChange={(e) => setName(e.target.value)} placeholder="Llaveros para Bar Pepe" required autoFocus />
                </label>
                <div className="flex flex-col gap-2">
                    <span className="label-text text-sm font-medium">Para quién es</span>
                    <div role="radiogroup" className="join">
                        {CUSTOMER_KINDS.map((item) => (
                            <button
                                key={item.value}
                                type="button"
                                role="radio"
                                aria-checked={kind === item.value}
                                className={`btn btn-sm join-item ${kind === item.value ? "btn-active btn-neutral" : ""}`}
                                onClick={() => { setKind(item.value); setCustomer(null); }}
                            >
                                {item.label}
                            </button>
                        ))}
                    </div>
                    <SearchPicker
                        key={kind}
                        value={customer?.id}
                        selectedLabel={customer?.label}
                        endpoint={config.endpoint}
                        paramsForQuery={(query) => ({ search: query || undefined, ...(kind === "customer_user" ? { kind: "customer" } : {}) })}
                        getOptionLabel={config.label_of}
                        onSelect={(option) => setCustomer(option ? { id: option.id, label: config.label_of(option) } : null)}
                    />
                </div>
                {error && <p role="alert" className="text-sm text-error">{error}</p>}
                <div className="modal-action mt-0">
                    <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>Cancelar</button>
                    <button type="submit" className="btn btn-primary btn-sm" disabled={!name.trim() || !customer || saving}>
                        {saving && <span className="loading loading-spinner loading-xs" aria-hidden="true" />}
                        Crear proyecto
                    </button>
                </div>
            </form>
            <button type="button" className="modal-backdrop" onClick={onClose} aria-label="Cerrar" />
        </div>
    );
}

function ProjectCard({ project, onOpen }) {
    const state = CUSTOMER_STATE[project.customer_state] || CUSTOMER_STATE.lead;
    return (
        <button
            type="button"
            onClick={onOpen}
            className="group text-left flex flex-col rounded-xl border border-base-content/10 bg-base-100 overflow-hidden hover:border-primary/50 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary"
        >
            <div className="grid grid-cols-4 gap-1 bg-base-200 p-2 h-24">
                {project.preview_images.length ? project.preview_images.map((url) => (
                    <img key={url} src={getMediaUrl(url)} alt="" className="w-full h-full object-contain" loading="lazy" />
                )) : <span className="col-span-4 self-center text-center text-xs text-base-content/50">Sin modelos todavía</span>}
            </div>
            <div className="p-4 flex flex-col gap-2">
                <div className="flex items-start justify-between gap-2">
                    <span className="font-semibold leading-snug group-hover:text-primary">{project.name}</span>
                    <StatusBadge label={projectStatusLabel(project.status)} tone={PROJECT_STATUS_TONE[project.status]} size="xs" />
                </div>
                <div className="flex items-center gap-2 text-sm text-base-content/75 min-w-0">
                    <span className="truncate">{project.customer_name || "Sin asignar"}</span>
                    <StatusBadge label={state.label} tone={state.tone} title={state.title} size="xs" outline />
                </div>
                <div className="flex items-center justify-between text-xs text-base-content/60">
                    <span>{project.item_count} {project.item_count === 1 ? "modelo" : "modelos"}{project.assigned_to_name ? `, ${project.assigned_to_name}` : ""}</span>
                    {project.catalog_link && (
                        <span className="inline-flex items-center gap-1" title="Visitas al catálogo">
                            <Eye className="w-3.5 h-3.5" aria-hidden="true" /> {project.catalog_link.view_count}
                        </span>
                    )}
                </div>
            </div>
        </button>
    );
}

/**
 * Admin › Proyectos (C3D-38): proyectos comerciales con su catálogo. El
 * detalle va en `?id=<id>` para no salir de la sección del panel. Desde leads
 * y clientes se llega con `?nuevo=lead:<id>` o `?nuevo=cliente:<id>`.
 */
export default function AdminProyectos() {
    const [searchParams, setSearchParams] = useSearchParams();
    const openId = Number(searchParams.get("id")) || null;
    const newParam = searchParams.get("nuevo");

    const [search, setSearch] = useState("");
    const [status, setStatus] = useState("");
    const [state, setState] = useState({ loading: true, error: false, projects: [], next: null });
    const [page, setPage] = useState(1);
    const [creating, setCreating] = useState(null);

    useEffect(() => {
        if (!newParam) return;
        const [kind, id] = newParam.split(":");
        setCreating({ kind, id: Number(id) || null, label: searchParams.get("nombre") || "" });
    }, [newParam, searchParams]);

    const load = useCallback((pageToLoad = 1) => {
        setState((prev) => ({ ...prev, loading: true, error: false }));
        axios.get("/projects/", { params: { search: search || undefined, status: status || undefined, page: pageToLoad } })
            .then(({ data }) => setState((prev) => ({
                loading: false,
                error: false,
                projects: pageToLoad === 1 ? data.results : [...prev.projects, ...data.results],
                next: data.next,
            })))
            .catch(() => setState((prev) => ({ ...prev, loading: false, error: true })));
        setPage(pageToLoad);
    }, [search, status]);

    useEffect(() => {
        if (openId) return undefined;
        const timer = setTimeout(() => load(1), 250);
        return () => clearTimeout(timer);
    }, [load, openId]);

    const open = (id) => setSearchParams({ id: String(id) });
    const closeDetail = () => setSearchParams({});

    if (openId) {
        return <ProyectoDetalle projectId={openId} onBack={closeDetail} onDeleted={closeDetail} />;
    }

    return (
        <div className="p-2 sm:p-4 flex flex-col gap-4">
            <header className="flex flex-wrap items-end justify-between gap-3">
                <div className="flex flex-col gap-1">
                    <h1 className="text-2xl font-bold">Proyectos</h1>
                    <p className="text-sm text-base-content/70 max-w-[65ch]">
                        Los modelos que preparáis para cada lead o cliente, con su catálogo para mandar por WhatsApp o en PDF.
                    </p>
                </div>
                <button type="button" className="btn btn-primary btn-sm gap-2" onClick={() => setCreating({ kind: "lead" })}>
                    <Plus className="w-4 h-4" aria-hidden="true" /> Nuevo proyecto
                </button>
            </header>

            <div className="flex flex-col sm:flex-row gap-2">
                <TextInput
                    leftIcon={<Search className="w-4 h-4" />}
                    placeholder="Buscar por proyecto, lead, cliente o email"
                    aria-label="Buscar proyectos"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    inputSize="sm"
                />
                <SelectDropdown options={STATUS_FILTERS} value={status} onChange={setStatus} size="sm" className="sm:w-56" />
            </div>

            {state.error && (
                <div role="alert" className="alert alert-error justify-between">
                    <span>No se han podido cargar los proyectos.</span>
                    <button type="button" className="btn btn-sm" onClick={() => load(1)}>Reintentar</button>
                </div>
            )}

            {state.loading && state.projects.length === 0 && (
                <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3" aria-busy="true">
                    {[0, 1, 2].map((key) => <div key={key} className="skeleton h-48 rounded-xl" />)}
                </div>
            )}

            {!state.loading && !state.error && state.projects.length === 0 && (
                <div className="flex flex-col items-center text-center gap-3 py-16 rounded-xl border border-dashed border-base-content/20">
                    <BriefcaseBusiness className="w-10 h-10 text-base-content/40" aria-hidden="true" />
                    <p className="font-semibold">{search || status ? "Ningún proyecto con estos filtros" : "Todavía no hay proyectos"}</p>
                    <p className="text-sm text-base-content/70 max-w-sm">
                        Crea uno para un lead o cliente y añádele los diseños que le hayas preparado.
                    </p>
                </div>
            )}

            {state.projects.length > 0 && (
                <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                    {state.projects.map((project) => (
                        <ProjectCard key={project.id} project={project} onOpen={() => open(project.id)} />
                    ))}
                </div>
            )}

            {state.next && (
                <button type="button" className="btn btn-ghost btn-sm self-center" onClick={() => load(page + 1)} disabled={state.loading}>
                    {state.loading && <span className="loading loading-spinner loading-xs" aria-hidden="true" />}
                    Cargar más
                </button>
            )}

            {creating && (
                <NewProjectModal
                    initial={creating}
                    onClose={() => { setCreating(null); if (newParam) setSearchParams({}); }}
                    onCreated={(project) => {
                        setCreating(null);
                        open(project.id);
                    }}
                />
            )}
        </div>
    );
}
